import React, { useEffect, useState } from 'react';
import { X, Heart, MessageCircle, Share2, ThumbsUp, Facebook, Loader2, CheckCircle2, CornerDownRight } from 'lucide-react';

const KIND_META = {
  comments: { title: 'Comments', Icon: MessageCircle, color: 'text-blue-400', bg: 'bg-blue-500/20' },
  reactions: { title: 'Likes & Reactions', Icon: Heart, color: 'text-pink-400', bg: 'bg-pink-500/20' },
  shares: { title: 'Shares', Icon: Share2, color: 'text-green-400', bg: 'bg-green-500/20' },
};

const REACTION_EMOJI = { LIKE: '👍', LOVE: '❤️', CARE: '🤗', HAHA: '😆', WOW: '😮', SAD: '😢', ANGRY: '😡' };

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return `${Math.floor(diff / 604800)}w`;
};

const Avatar = ({ src, name, size = 'w-9 h-9' }) => (
  <img src={src} alt={name} className={`${size} rounded-full object-cover flex-shrink-0 bg-white/10`} />
);

const CommentBody = ({ comment, pageId, isReply }) => {
  const isPage = comment.from?.id === pageId;
  return (
    <div className="min-w-0 flex-1">
      <div className="inline-block max-w-full rounded-2xl bg-white/[0.07] px-3.5 py-2">
        {isPage && <span className="block text-[10px] uppercase tracking-wide text-blue-300/70">Author</span>}
        <span className="text-sm font-semibold text-white flex items-center gap-1">
          {comment.from?.name}
          {isPage && <CheckCircle2 className="h-3.5 w-3.5 text-blue-400" />}
        </span>
        <p className="text-sm text-white/85 whitespace-pre-wrap break-words">{comment.message || <span className="italic text-white/40">(attachment)</span>}</p>
      </div>
      <div className="flex items-center gap-3 px-3 mt-1 text-xs text-white/40">
        <span>{timeAgo(comment.created_time)}</span>
        {comment.like_count > 0 && (
          <span className="flex items-center gap-1"><ThumbsUp className="h-3 w-3 text-blue-400" />{comment.like_count}</span>
        )}
        {!isReply && comment.reply_count > 0 && <span>{comment.reply_count} {comment.reply_count === 1 ? 'reply' : 'replies'}</span>}
      </div>
    </div>
  );
};

const ReplyBubble = ({ reply, pageId }) => (
  <div className="flex gap-2 ml-11 mt-2" data-testid="post-reply-item">
    <CornerDownRight className="h-4 w-4 text-white/20 mt-3 -ml-6 flex-shrink-0" />
    <Avatar src={reply.from?.picture} name={reply.from?.name} size="w-7 h-7" />
    <CommentBody comment={reply} pageId={pageId} isReply />
  </div>
);

const CommentBubble = ({ comment, pageId }) => (
  <div data-testid="post-comment-item">
    <div className="flex gap-2">
      <Avatar src={comment.from?.picture} name={comment.from?.name} />
      <CommentBody comment={comment} pageId={pageId} />
    </div>
    {comment.replies?.map((r) => <ReplyBubble key={r.id} reply={r} pageId={pageId} />)}
  </div>
);

const PersonRow = ({ item, right, sub }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-white/5 last:border-0" data-testid="post-person-item">
    <Avatar src={item.picture || item.from?.picture} name={item.name || item.from?.name} />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-white truncate">{item.name || item.from?.name}</p>
      {sub}
    </div>
    {right}
  </div>
);

export const PostEngagementDetailModal = ({ post, page, kind, accessToken, apiUrl, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const meta = KIND_META[kind];
  const KindIcon = meta.Icon;

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setData(null);
    fetch(`${apiUrl}/api/meta/posts/${post.id}/engagement-details?kind=${kind}&access_token=${encodeURIComponent(accessToken)}`)
      .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.detail || 'Failed to load'); return j; })
      .then((j) => { if (!cancelled) setData(j); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [post.id, kind, accessToken, apiUrl]);

  const p = data?.post || post;
  const items = data?.items || [];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="post-detail-modal">
      <div className="bg-slate-900 border border-white/20 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full ${meta.bg} flex items-center justify-center`}><KindIcon className={`h-4 w-4 ${meta.color}`} /></div>
            <h3 className="text-lg font-semibold text-white" data-testid="post-detail-title">{meta.title}</h3>
            {data && <span className={`text-sm font-bold ${meta.color}`} data-testid="post-detail-total">{(data.total || 0).toLocaleString()}</span>}
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/10 text-white/60 hover:text-white" data-testid="post-detail-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto custom-scrollbar flex-1">
          {/* Page + Post */}
          <div className="px-5 pt-4 pb-3 border-b border-white/10" data-testid="post-detail-post">
            <div className="flex items-center gap-3">
              <Avatar src={page?.picture} name={page?.name} size="w-10 h-10" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white flex items-center gap-1 truncate">
                  {page?.name || 'Facebook Page'} <CheckCircle2 className="h-3.5 w-3.5 text-blue-400" />
                </p>
                <p className="text-xs text-white/40">{p.created_time ? new Date(p.created_time).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : ''} · 🌐</p>
              </div>
            </div>
            {p.message && <p className="mt-3 text-sm text-white/90 whitespace-pre-wrap">{p.message}</p>}
            {p.thumbnail && <img src={p.thumbnail} alt="Post" className="mt-3 w-full max-h-64 object-cover rounded-xl border border-white/10" />}
            <div className="mt-3 flex items-center justify-between text-xs text-white/50">
              <span className="flex items-center gap-1"><ThumbsUp className="h-3.5 w-3.5 text-blue-400 fill-blue-400" /> {(p.likes || 0).toLocaleString()}</span>
              <span>{(p.comments || 0).toLocaleString()} comments · {(p.shares || 0).toLocaleString()} shares</span>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 py-4">
            {loading && <div className="flex items-center justify-center gap-2 py-8 text-white/50"><Loader2 className="h-5 w-5 animate-spin" /> Loading {meta.title.toLowerCase()}…</div>}
            {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl p-3" data-testid="post-detail-error">{error}</p>}

            {!loading && !error && kind === 'reactions' && (
              <>
                {Object.keys(data?.breakdown || {}).length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4" data-testid="reaction-breakdown">
                    {Object.entries(data.breakdown).sort((a, b) => b[1] - a[1]).map(([t, n]) => (
                      <span key={t} className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-sm text-white/80">{REACTION_EMOJI[t] || '👍'} {n}</span>
                    ))}
                  </div>
                )}
                {items.map((it) => (
                  <PersonRow key={it.id} item={it} right={<span className="text-xl">{REACTION_EMOJI[it.type] || '👍'}</span>} sub={<p className="text-xs text-white/40 capitalize">{(it.type || 'like').toLowerCase()}</p>} />
                ))}
                {items.length === 0 && <p className="text-sm text-white/40 text-center py-6">{data?.total > 0 ? 'Facebook only lists people who have granted your app permission; totals are shown above.' : 'No reactions yet.'}</p>}
              </>
            )}

            {!loading && !error && kind === 'comments' && (
              <div className="space-y-4">
                {items.map((c) => <CommentBubble key={c.id} comment={c} pageId={page?.id} />)}
                {items.length === 0 && <p className="text-sm text-white/40 text-center py-6">No comments yet.</p>}
              </div>
            )}

            {!loading && !error && kind === 'shares' && (
              <>
                {items.map((it) => (
                  <PersonRow key={it.id} item={it} right={<span className="text-xs text-white/40">{timeAgo(it.created_time)}</span>} sub={it.message ? <p className="text-sm text-white/70 whitespace-pre-wrap">{it.message}</p> : <p className="text-xs text-white/40">Shared without a caption</p>} />
                ))}
                {items.length === 0 && <p className="text-sm text-white/40 text-center py-6">{data?.total > 0 ? 'Facebook only lists public shares or shares from people who authorized your app.' : 'No shares yet.'}</p>}
              </>
            )}
          </div>
        </div>

        {p.permalink_url && (
          <div className="px-5 py-3 border-t border-white/10">
            <a href={p.permalink_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors" data-testid="post-detail-facebook-link">
              <Facebook className="h-4 w-4" /> View on Facebook
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
